import React, { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { ChevronRightFill, PlayCircle } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, Loader, Stack, Typography } from '@lyearn/ui';
import { Image } from '@/components/Image';
import { CharacterAvatarType } from '@/types/schema';

import AvatarPreviewModal from './AvatarPreviewModal';

interface AvatarGridViewProps {
  avatars: CharacterAvatarType[];
  selectedAvatarId: string;
  isLoading?: boolean;
  onBack: () => void;
  onSelectAvatar: (avatarId: string) => void;
  onPreviewAvatar?: (avatarId: string) => void;
}

const AvatarGridView = (props: AvatarGridViewProps) => {
  const { avatars, selectedAvatarId, isLoading, onBack, onSelectAvatar, onPreviewAvatar } = props;
  const { t } = useTranslation('Course');

  const [previewAvatarId, setPreviewAvatarId] = React.useState<string | null>(null);

  const previewAvatar = previewAvatarId
    ? avatars.find((a) => a.avatar_id === previewAvatarId) ?? null
    : null;

  const handleSelectAvatar = useCallback(
    (avatarId: string) => {
      setPreviewAvatarId(null);
      onSelectAvatar(avatarId);
    },
    [onSelectAvatar],
  );

  return (
    <>
      {/* Breadcrumb header */}
      <Stack alignItems="center" className="mb-16" direction="row" gap={4}>
        <Typography
          className="cursor-pointer"
          color="text-text-placeholder"
          variant="heading-s"
          onClick={onBack}>
          {t('Course:AiVideoGeneration.VideoSettings')}
        </Typography>
        <ChevronRightFill className="text-icon-placeholder" height={24} width={24} />
        <Typography color="text-text-primary" variant="heading-s">
          {t('Course:AiVideoGeneration.SelectAvatar')}
        </Typography>
      </Stack>

      {/* Avatar grid */}
      {isLoading && avatars.length === 0 ? (
        <Stack alignItems="center" className="h-[240px] w-full" justifyContent="center">
          <Loader />
        </Stack>
      ) : (
        <Box className="grid grid-cols-3 gap-12">
          {avatars.map((avatar) => {
            const isSelected = selectedAvatarId === avatar.avatar_id;

            return (
              <Box
                key={avatar.avatar_id}
                className={cn(
                  'relative shrink-0 cursor-pointer overflow-hidden rounded-12 border',
                  isSelected ? 'border-2 border-text-primary' : 'border-border',
                )}
                style={{ width: 169, height: 172 }}
                onClick={() => handleSelectAvatar(avatar.avatar_id)}>
                {/* Avatar image — fixed height */}
                <Box style={{ height: 142, overflow: 'hidden' }}>
                  <Image
                    alt={avatar.avatar_name}
                    className="h-full w-full object-cover object-top"
                    height={169}
                    src={avatar.preview_image_url || ''}
                    width={169}
                  />
                </Box>

                {/* Bottom info bar — 30px fixed height */}
                <Stack
                  alignItems="center"
                  className="w-full flex border-t border-border bg-bg-primary px-10"
                  direction="row"
                  justifyContent="between"
                  style={{ height: 30 }}>
                  <Typography
                    className="flex-1 min-w-0 truncate"
                    color="text-text-primary"
                    variant="body-short-02">
                    {avatar.avatar_name}
                  </Typography>
                  <Stack
                    className="shrink-0 cursor-pointer"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      if (onPreviewAvatar) {
                        onPreviewAvatar(avatar.avatar_id);
                      } else {
                        setPreviewAvatarId(avatar.avatar_id);
                      }
                    }}>
                    <PlayCircle color="icon-secondary" height={16} width={16} />
                  </Stack>
                </Stack>

                {/* Selected checkmark */}
                {isSelected ? (
                  <span className="absolute right-8 top-8 flex h-24 w-24 items-center justify-center rounded-full border border-divider-secondary bg-bg-primary text-14 font-semibold leading-none text-text-primary">
                    ✓
                  </span>
                ) : null}
              </Box>
            );
          })}
        </Box>
      )}

      {!onPreviewAvatar && previewAvatar ? (
        <AvatarPreviewModal
          avatar={previewAvatar}
          onChoose={() => handleSelectAvatar(previewAvatar.avatar_id)}
          onClose={() => setPreviewAvatarId(null)}
        />
      ) : null}
    </>
  );
};

export default AvatarGridView;
